import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function KickOverlay() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState("");
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    const targets = [document.documentElement, document.body, document.getElementById("root"), document.querySelector(".App")].filter(Boolean);
    targets.forEach((el) => el.style.setProperty("background", "transparent", "important"));
    return () => targets.forEach((el) => el.style.removeProperty("background"));
  }, []);

  useEffect(() => {
    let es = null;
    let cancelled = false;
    fetch(`${API}/api/kick/overlay/config?token=${token}`)
      .then((r) => { if (!r.ok) throw new Error("Geçersiz overlay linki"); return r.json(); })
      .then((cfg) => {
        if (cancelled) return;
        setConfig(cfg);
        const source = new EventSource(`${API}/api/kick/overlay/stream?token=${token}`);
        es = source;
        source.onmessage = (ev) => {
          if (cancelled) return;
          try {
            const alert = JSON.parse(ev.data);
            if (alert.type === "test") {
              queueRef.current = [];
              playingRef.current = false;
              if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
              if (videoRef.current) { videoRef.current.pause(); videoRef.current = null; }
              setCurrent(null);
              queueRef.current.push(alert);
              setTimeout(playNext, 80);
              return;
            }
            queueRef.current.push(alert);
            playNext();
          } catch {}
        };
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => {
      cancelled = true;
      if (es) es.close();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const playNext = () => {
    if (playingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    playingRef.current = true;
    next._seq = ++seqRef.current;
    setCurrent(next);
  };

  const finish = () => {
    playingRef.current = false;
    setCurrent(null);
    setTimeout(playNext, 300);
  };

  useEffect(() => {
    if (current && current.file_kind === "image") {
      timerRef.current = setTimeout(finish, (config?.image_duration ?? 6) * 1000);
      return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    const v = videoRef.current;
    return () => {
      if (v) {
        v.pause();
        v.removeAttribute("src");
        v.load();
        if (videoRef.current === v) videoRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (error) {
    return <div data-testid="kick-overlay-error" className="min-h-screen flex items-center justify-center text-red-400 text-sm font-mono">{error}</div>;
  }

  const effectiveVolume = current ? Math.max(0, Math.min(1, ((current.volume ?? 100) / 100) * ((config?.master_volume ?? 80) / 100))) : 0;

  return (
    <div data-testid="kick-overlay" className="min-h-screen w-full flex items-center justify-center" style={{ background: "transparent" }}>
      {current && current.file_url && (
        <div key={current._seq} style={{ animation: "kickPop 0.35s ease-out" }}>
          {current.file_kind === "video" ? (
            <video
              src={current.file_url}
              autoPlay
              playsInline
              onEnded={finish}
              onError={finish}
              data-testid="kick-overlay-video"
              style={{ maxWidth: "90vw", maxHeight: "90vh" }}
              ref={(el) => { videoRef.current = el; if (el) { el.volume = effectiveVolume; el.play().catch(() => {}); } }}
            />
          ) : (
            <img src={current.file_url} alt="" data-testid="kick-overlay-image" style={{ maxWidth: "90vw", maxHeight: "90vh" }} />
          )}
          <style>{`@keyframes kickPop { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
}
